import type { GetStaticPaths, NextPage } from 'next'
import React from 'react'

import { useApiData } from '@hozana/api/hooks/useApiData'
import { selectQueryData } from '@hozana/api/selectors'
import { createGetStaticData, createGetStaticProps } from '@hozana/api/server/static'
import type { TWrappedStaticContext } from '@hozana/redux/types'
import { useRouter } from '@hozana/router'

import { Head } from 'general/structure/Head'
import { Page } from 'general/structure/Page'
import { NAMESPACE } from 'i18n/constants'
import { IMMUTABLE_PREFIX, PAGE } from 'routes/constants'

import { GuideBody } from 'modules/guide/components/GuideBody'
import { getGuideQuery } from 'modules/guide/queries'

type TGuideContext = TWrappedStaticContext<{ guideName: string; subPathParts: string | string[] }>

const buildGuideQuery = ({ params: { guideName, subPathParts } }: TGuideContext) =>
  getGuideQuery({
    path: ['', guideName, ...(typeof subPathParts === 'string' ? [subPathParts] : subPathParts || [])].join('/'),
  })
const getGuidePath = (guideName: string, subPathParts?: string | string[]) =>
  ['', guideName, ...(typeof subPathParts === 'string' ? [subPathParts] : subPathParts || [])].join('/')

export const getStaticProps = createGetStaticProps(
  PAGE.GUIDE,
  (context: TGuideContext) => selectQueryData(context.store.getState(), buildGuideQuery(context))?.title,
  createGetStaticData((context: TGuideContext) => ({
    guide: {
      query: buildGuideQuery(context),
      displayErrors: true,
    },
  })),
  { namespaces: [NAMESPACE.GUIDE, NAMESPACE.COMMUNITY] },
)

/**
 * ### GuidePage ###
 *
 * Common page component for guide pages.
 */
export const GuidePage: NextPage = () => {
  const {
    locale,
    query: { guideName, subPathParts },
  } = useRouter()
  const path = getGuidePath(guideName as string, subPathParts)

  const [guide] = useApiData(() => ({ query: getGuideQuery({ path }) }), [path])

  return (
    <Page metaTitle={guide?.metaTitle}>
      <Head
        description={guide?.metaDescription || undefined}
        isIndexed={guideName !== 'live'}
        links={{
          canonical: `${CONF_KEYS.HOME}${locale === 'fr' ? '' : '/' + locale}${path}`,
        }}
        fontUrl="https://fonts.googleapis.com/css?family=Noto+Serif:400,700,400italic"
      />
      {/* Bare script is needed because GuidePage does not ship with JS */}
      {/* eslint-disable-next-line @next/next/no-sync-scripts */}
      <script src={`${IMMUTABLE_PREFIX}/js/full-ssr-functions.js`} type="text/javascript" />
      {guide && <GuideBody guide={guide} guideName={guideName as string} />}
    </Page>
  )
}

export const getStaticPaths: GetStaticPaths<{ guideName: string; subPathParts: string | string[] }> = async () => ({
  paths: [],
  fallback: 'blocking',
})
